/**
 * `loadSourceAuth` — fetch the decrypted `SourceAuth` blob for a given
 * registered source, with graceful fallback to `undefined` whenever
 * auth isn't available.
 *
 * Returns `undefined` (not throws) when:
 *
 *   - the source doesn't support login (`capabilities.login === false`);
 *   - the `PassphraseSession` is locked, so we can't decrypt anything;
 *   - no local or account-synced credentials exist for this source;
 *   - decryption throws (usually a passphrase mismatch).
 *
 * Returns the `SourceAuth` blob otherwise.
 *
 * Why this exists: every page that calls into a source module
 * (`SourcePage`, `ComicPage`, `Reader`) needs to forward the user's
 * cookies into `getPopular` / `getComic` / `getPages` / `resolveImageUrl`.
 * Some sources tolerate an anonymous request (e-hentai, hitomi), but
 * exhentai redirect-loops anon visitors — the Cloudflare worker can't
 * resolve the loop and surfaces it as a 502 to the SPA.
 * Centralizing the load + the "swallow failures" policy here keeps
 * every call site short.
 *
 * Note: runtime deps (`isUnlocked`, `load`) are passed explicitly so
 * this module has no value imports from `@/`-aliased paths and is
 * directly runnable under `node --test --experimental-strip-types`.
 * The real `PassphraseSession.isUnlocked` / `getAuth` wiring lives in
 * `useSourceAuth.ts`, which is only ever imported through Vite.
 */

import type { ComicSource, SourceAuth } from '@/sources/types';

export interface LoadSourceAuthDeps {
  /** Returns `true` when `PassphraseSession` holds a derived key. */
  isUnlocked: () => boolean;
  /**
   * Decrypt the saved auth row for `sourceId`. Returns `undefined` if
   * no row exists; throws if decryption fails (passphrase mismatch).
   */
  load: (sourceId: string) => Promise<SourceAuth | undefined>;
  loadAccountSynced?: (sourceId: string) => Promise<SourceAuth | undefined>;
}

export async function loadSourceAuth(
  source: ComicSource,
  deps: LoadSourceAuthDeps,
): Promise<SourceAuth | undefined> {
  if (!source.capabilities.login) return undefined;
  let accountFallback: SourceAuth | undefined;
  if (!deps.isUnlocked()) {
    try {
      accountFallback = await deps.loadAccountSynced?.(source.id);
    } catch {
      accountFallback = undefined;
    }
    if (!deps.isUnlocked()) return accountFallback;
  }
  try {
    const local = await deps.load(source.id);
    if (local) return local;
  } catch {
    // Decryption failed, so account sync remains the recovery path.
  }
  if (accountFallback) return accountFallback;
  try {
    return await deps.loadAccountSynced?.(source.id);
  } catch {
    return undefined;
  }
}
